import { ClientType } from './portfolio'

/**
 * Who-we-serve audience segment pages. One entry per ICP, one static route per
 * entry under /who-we-help/{slug} (prerenderer skips param routes, so each page
 * is a thin wrapper component). The /who-we-help index remains the overview.
 *
 * Claims discipline: capabilities listed here are live consulting capabilities
 * (bespoke sites, CMS editing, application funnels with payments, event
 * calendars, email integration). No unshipped platform modules.
 */

export interface AudienceSegment {
  slug: string
  /** Nav + card label, e.g. "Music Festivals" */
  name: string
  seoTitle: string
  seoDescription: string
  heroHeadline: string
  heroBody: string
  painPoints: { title: string; description: string }[]
  whatWeBuild: { title: string; description: string }[]
  caseStudies: { label: string; href: string }[]
  /** Portfolio ClientTypes used to surface real project cards on the page. */
  portfolioTypes: ClientType[]
  faqs: { question: string; answer: string }[]
  ctaHeading: string
  ctaBody: string
}

export const audienceSegments: AudienceSegment[] = [
  {
    slug: 'music-festivals',
    name: 'Music Festivals',
    seoTitle: 'Websites & Application Systems for Music Festivals',
    seoDescription:
      'Fugue Lab builds websites and application systems for classical music festivals and summer programs. Original design, music-native application funnels, and a team that runs it with you.',
    heroHeadline: 'Your festival, on a website that fills the roster',
    heroBody:
      'You run auditions, faculty, housing, repertoire, and a season, usually with a staff you can count on one hand. Your website and application system should carry some of that weight instead of adding to it.',
    painPoints: [
      {
        title: 'Applications live somewhere else',
        description:
          'Applicants leave your site for a third-party portal or a Google Form. You pay per applicant, or you chase spreadsheets, and the experience says nothing about your program.',
      },
      {
        title: 'The site goes stale mid-season',
        description:
          'Faculty changes, schedule updates, and program announcements wait on whoever knows how to edit the site. By August it is describing last year.',
      },
      {
        title: 'Prospective students bounce on mobile',
        description:
          'Seventeen-year-olds evaluate your festival on their phones. A slow, dated site reads as a slow, dated program, whatever the truth is.',
      },
      {
        title: 'Nothing talks to anything',
        description:
          'Inquiries in one inbox, applications in another tool, payments in a third. Reconciling who applied, who paid, and who enrolled eats staff weeks.',
      },
    ],
    whatWeBuild: [
      {
        title: 'A festival website designed for you',
        description:
          'Programs, faculty, schedules, and repertoire as first-class structure, in a design that matches the level of your teaching.',
      },
      {
        title: 'Application and audition funnels',
        description:
          'Music-native forms with repertoire lists, audition video upload, recommendations, and integrated payments, on your own domain.',
      },
      {
        title: 'A CMS your team actually uses',
        description:
          'Update faculty, dates, and programs yourself in minutes. The design stays intact no matter who edits.',
      },
      {
        title: 'Applicant and admit communications',
        description: 'Email sequences that keep applicants moving from inquiry to application to deposit.',
      },
      {
        title: 'Enrollment analytics',
        description:
          'Where applicants come from, where they stall, and what your funnel actually converts, in numbers you can act on.',
      },
    ],
    caseStudies: [
      {
        label: 'How Chroma International Music Festival modernized its applications',
        href: '/blog/chroma-festival-case-study',
      },
      { label: 'Why festivals are losing applicants', href: '/blog/festivals-losing-applicants' },
    ],
    portfolioTypes: [ClientType.Festival],
    faqs: [
      {
        question: 'Our applications open every fall. When should we start?',
        answer:
          'Count backwards from your open date: a full site plus application build wants a few months of lead time, an application funnel alone can move faster. Spring and summer are when most festivals make the switch.',
      },
      {
        question: 'We already have a designer. Do we need the full package?',
        answer:
          'No. Festivals with internal design capacity take the platform work alone, and the site plays to your designer instead of replacing them.',
      },
      {
        question: 'Can you match how our current application works?',
        answer:
          'We start by documenting your current fields, fees, deadlines, and review steps, then rebuild that exactly before improving anything. Your team approves the forms before applicants see them.',
      },
    ],
    ctaHeading: 'Talk through your next season',
    ctaBody:
      'Book a call and bring your current site and application setup. You leave with a concrete read on what a rebuild involves and when it would need to start.',
  },
  {
    slug: 'choral-festivals',
    name: 'Choral Festivals & Choruses',
    seoTitle: 'Websites for Choral Festivals & Community Choruses',
    seoDescription:
      'Fugue Lab builds websites for choral festivals, community choruses, and vocal ensembles. Concert seasons, singer recruitment, and festival applications, built by a working choral musician.',
    heroHeadline: 'A website worthy of the sound you make',
    heroBody:
      'Choral organizations run on volunteers, goodwill, and a season that never stops. The website is usually maintained by whoever offered in 2019. It deserves the same care as your programming, without needing another volunteer to carry it.',
    painPoints: [
      {
        title: 'Singer recruitment is word of mouth only',
        description:
          'Auditions are announced in the concert program and an email list. A singer who googles you finds a site that does not say how to join.',
      },
      {
        title: 'Concert details arrive late and scattered',
        description:
          'Dates on the site, tickets in an email, program notes in a PDF. Your audience pieces it together or gives up.',
      },
      {
        title: 'Festival applications on paper and attachments',
        description:
          'Participating choirs email PDFs and recordings to a director who then builds the spreadsheet by hand.',
      },
      {
        title: 'The volunteer webmaster problem',
        description: 'One person knows how the site works. When they step back, the site freezes in time.',
      },
    ],
    whatWeBuild: [
      {
        title: 'A season-first website',
        description:
          'Concerts, venues, repertoire, and program notes structured as a season your audience can actually follow.',
      },
      {
        title: 'Audition and membership funnels',
        description: 'A clear join path for singers: voice parts, expectations, audition signup, all on the site.',
      },
      {
        title: 'Festival and honor-choir applications',
        description: 'Choir and singer applications with recordings, repertoire, and payments handled in one flow.',
      },
      {
        title: 'Editing any board member can do',
        description: 'The CMS is scoped to your content. Next season goes up without a webmaster.',
      },
      {
        title: 'Email list integration',
        description:
          'Signup forms that feed the list you already use, so concert announcements reach people who asked for them.',
      },
    ],
    caseStudies: [
      { label: 'The Wendell Whalum Great Host of Composers Festival', href: '/blog/wendell-whalum-case-study' },
      { label: 'Holy Trinity: a parish music program online', href: '/blog/holy-trinity-case-study' },
    ],
    portfolioTypes: [ClientType.Ensemble, ClientType.Church, ClientType.Festival],
    faqs: [
      {
        question: 'We are volunteer-run with a small budget. Is this realistic?',
        answer:
          'Packages scale down for volunteer organizations, and the build is scoped to what moves the needle: usually the season, the join path, and editing your board can own. The pricing page has the bands, and the call gives you a real number.',
      },
      {
        question: 'Who maintains it after launch?',
        answer:
          'We do. Content edits stay in your hands through the CMS, and the technical side stops being a volunteer job.',
      },
      {
        question: 'You actually know choral music?',
        answer:
          'Fugue Lab is run by a working church musician and conductor. Voice parts, hymn boards, festival adjudication, and Evensong schedules do not need explaining first.',
      },
    ],
    ctaHeading: 'Give the chorus a real home online',
    ctaBody:
      'Book a call, bring your current site, and talk to someone who has programmed a choral season. You get an honest scope for what your organization actually needs.',
  },
  {
    slug: 'young-artist-programs',
    name: 'Young Artist & Conducting Programs',
    seoTitle: 'Application Systems for Young Artist & Conducting Programs',
    seoDescription:
      'Fugue Lab builds websites and audition application systems for young artist programs, conducting programs, and residencies. Per-movement video, repertoire fields, and no per-applicant fees.',
    heroHeadline: 'Auditions are your front door. Build it like one.',
    heroBody:
      'A young artist program lives or dies on the quality of its applicant pool. The application experience is the first interaction every serious candidate has with you, and per-applicant platform fees quietly punish you for attracting more of them.',
    painPoints: [
      {
        title: 'Per-applicant fees scale against you',
        description:
          'On incumbent platforms, a stronger recruiting year means a bigger bill. Small cohorts pay enterprise prices per head.',
      },
      {
        title: 'Audition video is an afterthought',
        description:
          'Generic form tools choke on large video files, and reviewers end up chasing links across YouTube, Drive, and email.',
      },
      {
        title: 'The program page undersells the program',
        description:
          'Alumni placements, faculty, and repertoire live in a PDF, while the site itself says nothing a candidate could not guess.',
      },
      {
        title: 'Review season is spreadsheet season',
        description:
          'Faculty adjudicate from shared folders and email threads, and the admin rebuilds the tracking sheet nightly.',
      },
    ],
    whatWeBuild: [
      {
        title: 'Audition applications built for music',
        description:
          'Repertoire lists, per-movement video upload, teacher recommendations, and instrument or voice-part routing as native fields.',
      },
      {
        title: 'Reliable large-file video handling',
        description:
          'Applicants upload audition video directly in the form, and reviewers watch it inside the review flow.',
      },
      {
        title: 'A program site that recruits',
        description:
          'Faculty, alumni outcomes, past repertoire, and program life presented the way a candidate evaluates them.',
      },
      {
        title: 'Review workflow for your panel',
        description:
          'Every application, its materials, and its status in one place your faculty can use without training.',
      },
      {
        title: 'Flat pricing',
        description: 'No per-applicant fees. A great recruiting year costs the same as a slow one.',
      },
    ],
    caseStudies: [
      {
        label: 'How Chroma International Music Festival modernized its applications',
        href: '/blog/chroma-festival-case-study',
      },
      { label: 'Why festivals are losing applicants', href: '/blog/festivals-losing-applicants' },
    ],
    portfolioTypes: [ClientType.Festival, ClientType.Conservatory],
    faqs: [
      {
        question: 'Our cohort is 12 singers. Is a custom system overkill?',
        answer:
          'Small cohorts are exactly where per-applicant platforms hurt most: fixed enterprise costs spread over a dozen admits. A right-sized funnel with flat pricing usually costs less than what you pay now.',
      },
      {
        question: 'Can faculty review remotely?',
        answer:
          'Yes. Reviewers work from anywhere with a browser: materials, video, and scoring in one place instead of a shared drive.',
      },
      {
        question: 'We route applicants across multiple programs. Can the form handle it?',
        answer:
          'Yes. Instrument, voice part, and program routing decide which fields and fees an applicant sees, so one application serves the whole umbrella.',
      },
    ],
    ctaHeading: 'Make the application match the program',
    ctaBody:
      'Book a call and walk through your current audition process, platform bill included. You leave knowing exactly what a flat-priced rebuild looks like.',
  },
  {
    slug: 'pre-college-summer-programs',
    name: 'Pre-College & Summer Programs',
    seoTitle: 'Websites & Admissions Funnels for Pre-College Music Programs',
    seoDescription:
      'Fugue Lab builds admissions funnels and program sites for conservatory pre-college divisions and summer intensives. Multi-program routing, parent-facing clarity, and integrated payments.',
    heroHeadline: 'Admissions infrastructure for programs parents trust',
    heroBody:
      'Pre-college and summer programs sell to two audiences at once: a teenager choosing where to spend a summer, and a parent wiring the deposit. Your site and application need to convince both, and your admissions office needs the back end to not be forty spreadsheet tabs.',
    painPoints: [
      {
        title: 'Multiple programs, one tangled application',
        description:
          'Strings intensive, composition week, jazz workshop: each with its own dates, fees, and prerequisites, crammed into one generic form or split across five.',
      },
      {
        title: 'Parents cannot find the practical answers',
        description:
          'Housing, supervision, dates, and cost hide in PDFs. Every gap becomes a phone call to your office.',
      },
      {
        title: 'Deposits and scholarships tracked by hand',
        description:
          'Who paid, who deferred, who got aid: reconciled manually between the form tool, the payment processor, and the registrar sheet.',
      },
      {
        title: 'Institutional systems that cannot flex',
        description:
          'The university CMS was not built for a summer program with a March deadline, and IT tickets move on IT time.',
      },
    ],
    whatWeBuild: [
      {
        title: 'Multi-program admissions funnels',
        description:
          'One application that routes by program, instrument, and age group, with the right fields, fees, and deadlines for each.',
      },
      {
        title: 'Parent-facing program pages',
        description: 'Dates, housing, supervision, cost, and outcomes answered directly, so the phone rings less.',
      },
      {
        title: 'Integrated payments and deposits',
        description: 'Application fees and enrollment deposits collected in the flow and reconciled in one place.',
      },
      {
        title: 'Scholarship and aid tracking',
        description: 'Aid requests and awards recorded on the application itself, not a side spreadsheet.',
      },
      {
        title: 'Independence from campus IT',
        description: 'Your program site runs on its own stack, updated by your staff through a CMS, on your timeline.',
      },
    ],
    caseStudies: [
      {
        label: 'How Chroma International Music Festival modernized its applications',
        href: '/blog/chroma-festival-case-study',
      },
    ],
    portfolioTypes: [ClientType.Festival, ClientType.University, ClientType.Conservatory],
    faqs: [
      {
        question: 'We are part of a university. Can we even use an outside vendor?',
        answer:
          'Summer and pre-college divisions commonly run their own web and admissions tooling, and the build can live on a subdomain your institution controls. Procurement questions get answered directly on the call.',
      },
      {
        question: 'Can it handle minors and guardian consent?',
        answer:
          'Yes. Guardian contact and consent are built into the application flow for applicants under 18, and we scope data handling with your policies during discovery.',
      },
      {
        question: 'Our enrollment window is short and intense. Will the system hold up?',
        answer:
          'The sites are prerendered static pages that do not buckle under deadline-day traffic, and the application stack is built on the same serverless infrastructure that scales with load.',
      },
    ],
    ctaHeading: 'Fix admissions before the next cycle',
    ctaBody:
      'Book a call with your current application process in hand. You get a program-by-program plan for what a unified funnel would look like.',
  },
  {
    slug: 'churches-sacred-music',
    name: 'Churches & Sacred Music',
    seoTitle: 'Websites for Church Music Programs & Sacred Music Series',
    seoDescription:
      'Fugue Lab builds websites for parishes, church music programs, and sacred music concert series. Built by a working church musician who knows the liturgy from the bench.',
    heroHeadline: 'A parish website built by someone who has played the liturgy',
    heroBody:
      'Your music program is often the most public thing your parish does: the concert series, Evensong, the choir that visitors mention on the way out. The website should reflect that, and it should be updatable by parish staff without a call to anyone.',
    painPoints: [
      {
        title: 'The site is years out of date',
        description:
          'Service times from two rectors ago, a choir page with former members, and a music series buried three clicks deep.',
      },
      {
        title: 'Concert series with no box office path',
        description:
          'Sacred music concerts announced in the bulletin and Facebook, with ticket or donation links scattered across whatever was set up first.',
      },
      {
        title: 'Choir recruitment depends on the narthex',
        description:
          'A singer searching for a choir in your city will not find you, and if they do, the site does not say when rehearsals are or who to email.',
      },
      {
        title: 'Nobody on staff can edit it',
        description:
          'The parish administrator has fourteen jobs already, and the website is the one with the login nobody remembers.',
      },
    ],
    whatWeBuild: [
      {
        title: 'A parish site for the whole parish',
        description:
          'Services, ministries, staff, and music presented together. The music program shines without the rest of parish life disappearing behind it.',
      },
      {
        title: 'Concert series and event calendars',
        description:
          'Your sacred music series with dates, programs, and performers, structured so the next season is an edit, not a rebuild.',
      },
      {
        title: 'Choir and ensemble pages that recruit',
        description:
          'Rehearsal times, repertoire, expectations, and a direct way for a new singer to reach the director.',
      },
      {
        title: 'Editing for parish staff',
        description: 'Service times, bulletins, and announcements updated by the office in minutes through the CMS.',
      },
      {
        title: 'Giving and ticketing links, integrated cleanly',
        description:
          'Your existing giving platform and box office connected coherently from the site, instead of a pile of mismatched buttons.',
      },
    ],
    caseStudies: [{ label: 'Holy Trinity: a parish music program online', href: '/blog/holy-trinity-case-study' }],
    portfolioTypes: [ClientType.Church, ClientType.Organist],
    faqs: [
      {
        question: 'Is this just for the music program, or the whole parish site?',
        answer:
          'The whole parish. A music-only microsite strands the rest of parish life on the old site, so we build the full site with the music program given the prominence it earns.',
      },
      {
        question: 'We have almost no budget between pledge seasons.',
        answer:
          'Parish builds are scoped accordingly, and vestries usually find the number closer to a music-fund line item than a capital campaign. The pricing page has the bands.',
      },
      {
        question: 'Do you understand liturgical scheduling?',
        answer:
          'Fugue Lab is run by a working church musician. Feast-day transferals, seasonal service changes, and why the Easter schedule cannot go up late are already understood.',
      },
    ],
    ctaHeading: 'Put the parish online properly',
    ctaBody:
      'Book a call, bring your current site, and talk to a church musician who builds websites. You get a scope the vestry can read.',
  },
  {
    slug: 'music-schools',
    name: 'Music Schools & Studios',
    seoTitle: 'Websites for Community Music Schools & Teaching Studios',
    seoDescription:
      'Fugue Lab builds websites and inquiry funnels for community music schools and multi-teacher studios. Trial-lesson funnels, faculty pages, and a site that converts parents into enrolled students.',
    heroHeadline: 'The website is your best-performing teacher',
    heroBody:
      'Every enrolled student starts as a parent searching "piano lessons near me" at 9pm. Your site either converts that search into a trial lesson or hands the student to the school down the street with the better one.',
    painPoints: [
      {
        title: 'Inquiries leak between tools',
        description:
          'The contact form emails one inbox, the front desk logs another, and nobody knows how many trial requests died unanswered last month.',
      },
      {
        title: 'Faculty pages that undersell the teaching',
        description: 'Your teachers are conservatory-trained performers, and the site shows a name and an instrument.',
      },
      {
        title: 'The site does not answer the parent questions',
        description:
          'Rates, ages, instruments, scheduling, and what a first lesson looks like. Gaps become phone calls or lost students.',
      },
      {
        title: 'Growth is invisible',
        description: 'You spend on ads and open houses with no idea which source actually produces enrolled students.',
      },
    ],
    whatWeBuild: [
      {
        title: 'A school site built to convert parents',
        description: 'Programs, faculty, rates, and the trial-lesson path presented the way a parent actually shops.',
      },
      {
        title: 'Trial-lesson inquiry funnels',
        description:
          'Instrument, age, and availability captured up front, routed to the right person, with nothing falling through.',
      },
      {
        title: 'Faculty pages worth the hires',
        description: 'Bios, credentials, media, and studio openings for every teacher, editable as the roster changes.',
      },
      {
        title: 'Enrollment source tracking',
        description: 'Which searches, pages, and campaigns produce actual trial lessons, in analytics you can read.',
      },
      {
        title: 'Recital and event calendars',
        description: 'Studio recitals, masterclasses, and registration windows on a calendar parents check.',
      },
    ],
    caseStudies: [
      {
        label: 'How Chroma International Music Festival modernized its applications',
        href: '/blog/chroma-festival-case-study',
      },
    ],
    portfolioTypes: [ClientType.School, ClientType.MusicSchool, ClientType.Educator],
    faqs: [
      {
        question: 'We use scheduling software already. Does the site replace it?',
        answer:
          'No, it feeds it. The site handles discovery and the trial-lesson funnel, then hands enrolled students to whatever scheduling and billing system you run.',
      },
      {
        question: 'How is this different from the template our old designer used?',
        answer:
          'The build starts from how parents choose a music school, not from a theme. Faculty, programs, and the inquiry funnel are structured, measurable, and editable by your front desk.',
      },
      {
        question: 'Can you work with a school our size?',
        answer:
          'The work is scoped to studio size, from a multi-teacher studio to a school with hundreds of students. The call establishes which package fits before anything is proposed.',
      },
    ],
    ctaHeading: 'Turn searches into trial lessons',
    ctaBody:
      'Book a call and bring last month’s inquiry numbers if you have them. You get a clear picture of where students are leaking and what a funnel that holds them looks like.',
  },
]

export function getAudienceSegment(slug: string): AudienceSegment {
  const segment = audienceSegments.find((s) => s.slug === slug)
  if (!segment) throw new Error(`Unknown audience segment slug: ${slug}`)
  return segment
}
